/**
 * CredentialsSection — iter100.319
 *
 * Doctor-facing UI for managing Certifications, Awards, and Publications.
 * Lives inside the "Certifications & Achievements" accordion on
 * /doctor/profile. State is a plain array of
 *   { id, title, issuer, year, kind }
 * kept in the parent form; this component just renders + edits it and
 * calls `onChange(newArray)` on every mutation. Saving happens on the
 * parent's global "Save changes" button.
 *
 * Also renders the two AUTO-BADGES at the top (Invisique™ Certified +
 * DCI Verified) — those are locked, sourced from server-side signals
 * (`lifecycle_status` + `dci_registration_number`) and NOT part of the
 * editable array.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Certificate, Trophy, BookOpen, ShieldCheck, PencilSimple, Trash, Plus,
    Medal, Check,
} from "@phosphor-icons/react";


const KIND_META = {
    certification: {
        label: "Certification",
        icon: Certificate,
        chipClass: "bg-blue-100 text-blue-800",
        iconBg: "bg-blue-50",
        iconColor: "text-blue-600",
    },
    award: {
        label: "Award",
        icon: Medal,
        chipClass: "bg-amber-100 text-amber-800",
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
    },
    publication: {
        label: "Publication",
        icon: BookOpen,
        chipClass: "bg-violet-100 text-violet-800",
        iconBg: "bg-violet-50",
        iconColor: "text-violet-600",
    },
};


function newId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `cred-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}


/* AutoBadge — iter100.326 (redesigned as trust card)
 * White card inside the emerald wash with a floating "✓ VERIFIED"
 * corner pill so patients see instantly which credentials are
 * server-verified (vs. self-declared below). */
function AutoBadge({ icon: Icon, label, sublabel }) {
    return (
        <div className="relative flex items-center gap-3 rounded-xl bg-white border border-emerald-200 px-4 py-3.5">
            <span className="absolute -top-2 right-3 inline-flex items-center gap-1 rounded bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">
                <Check size={9} weight="bold" /> Verified
            </span>
            <span className="shrink-0 w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center">
                <Icon size={18} weight="fill" />
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-emerald-950 leading-tight">{label}</div>
                <div className="text-[11.5px] text-emerald-700 mt-0.5 truncate">{sublabel}</div>
            </div>
        </div>
    );
}


function CredentialRow({ row, onEdit, onDelete }) {
    const meta = KIND_META[row.kind] || KIND_META.certification;
    const Icon = meta.icon;
    return (
        <div
            className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-3.5 py-3 hover:border-primary/40 hover:shadow-[0_2px_8px_rgba(6,182,212,0.08)] transition-all"
            data-testid={`cred-row-${row.id}`}
        >
            <span className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center ${meta.iconBg} ${meta.iconColor}`}>
                <Icon size={20} weight="duotone" />
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-bold text-foreground leading-tight truncate">
                    {row.title}
                </div>
                {row.issuer && (
                    <div className="text-[12px] text-muted-foreground mt-0.5 truncate">
                        {row.issuer}
                    </div>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                    {row.year && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                            {row.year}
                        </span>
                    )}
                    <span className={`rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-widest ${meta.chipClass}`}>
                        {meta.label}
                    </span>
                </div>
            </div>
            {/* Actions — hidden by default, revealed on hover */}
            <div className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    type="button"
                    onClick={() => onEdit(row)}
                    className="h-8 w-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-primary hover:border-primary/40 hover:bg-sky-50 transition-colors"
                    aria-label="Edit"
                    data-testid={`cred-edit-${row.id}`}
                >
                    <PencilSimple size={14} weight="bold" />
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(row)}
                    className="h-8 w-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
                    aria-label="Delete"
                    data-testid={`cred-delete-${row.id}`}
                >
                    <Trash size={14} weight="bold" />
                </button>
            </div>
        </div>
    );
}


function CredentialDialog({ open, onOpenChange, initial, onSave }) {
    const [title, setTitle] = useState(initial?.title || "");
    const [issuer, setIssuer] = useState(initial?.issuer || "");
    const [year, setYear] = useState(initial?.year || "");
    const [kind, setKind] = useState(initial?.kind || "certification");

    // Reset local state when dialog re-opens with a different row
    React.useEffect(() => {
        if (open) {
            setTitle(initial?.title || "");
            setIssuer(initial?.issuer || "");
            setYear(initial?.year || "");
            setKind(initial?.kind || "certification");
        }
    }, [open, initial]);

    const submit = () => {
        const t = title.trim();
        if (!t) {
            toast.error("Title is required — e.g. 'Fellow, Indian Orthodontic Society'");
            return;
        }
        const yr = year ? Number(year) : "";
        if (yr !== "" && (Number.isNaN(yr) || yr < 1950 || yr > new Date().getFullYear() + 1)) {
            toast.error("Year must be between 1950 and next year");
            return;
        }
        onSave({
            id: initial?.id || newId(),
            title: t,
            issuer: issuer.trim(),
            year: yr || null,
            kind,
        });
        onOpenChange(false);
    };

    const currentYear = new Date().getFullYear();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="cred-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {initial ? "Edit credential" : "Add a credential"}
                    </DialogTitle>
                    <DialogDescription>
                        Certifications, awards, or publications you&apos;d like patients to see on your public profile.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div>
                        <Label htmlFor="cred-title" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                            Title
                        </Label>
                        <Input
                            id="cred-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Fellow, Indian Orthodontic Society"
                            maxLength={160}
                            className="mt-1 h-10"
                            autoFocus
                            data-testid="cred-input-title"
                        />
                    </div>

                    <div>
                        <Label htmlFor="cred-issuer" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                            Issuer / Publisher
                        </Label>
                        <Input
                            id="cred-issuer"
                            value={issuer}
                            onChange={(e) => setIssuer(e.target.value)}
                            placeholder="IOS · New Delhi"
                            maxLength={160}
                            className="mt-1 h-10"
                            data-testid="cred-input-issuer"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="cred-year" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Year
                            </Label>
                            <Input
                                id="cred-year"
                                type="number"
                                inputMode="numeric"
                                value={year}
                                onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                placeholder={String(currentYear)}
                                min="1950"
                                max={currentYear + 1}
                                className="mt-1 h-10"
                                data-testid="cred-input-year"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Type
                            </Label>
                            <div className="mt-1 grid grid-cols-3 gap-1 h-10 items-center rounded-md border border-input p-0.5">
                                {["certification", "award", "publication"].map((k) => {
                                    const label = KIND_META[k].label;
                                    const active = kind === k;
                                    return (
                                        <button
                                            key={k}
                                            type="button"
                                            onClick={() => setKind(k)}
                                            className={`h-full rounded text-[10.5px] font-bold uppercase tracking-wider transition-colors ${
                                                active
                                                    ? "bg-primary text-primary-foreground shadow-sm"
                                                    : "text-muted-foreground hover:text-foreground"
                                            }`}
                                            data-testid={`cred-input-kind-${k}`}
                                        >
                                            {label.slice(0, 4)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cred-dialog-cancel">
                        Cancel
                    </Button>
                    <Button onClick={submit} data-testid="cred-dialog-save">
                        {initial ? "Update" : "Add"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


export default function CredentialsSection({
    credentials = [],
    onChange,
    // Auto-badges (read-only, server-driven)
    invisiqueCertified = false,
    dciRegNumber = "",
    stateCouncil = "",
}) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    // Sort: recent year first, then alphabetical for undated
    const sorted = [...credentials].sort((a, b) => {
        const ay = a.year || 0;
        const by = b.year || 0;
        if (ay !== by) return by - ay;
        return (a.title || "").localeCompare(b.title || "");
    });

    const openAdd = () => {
        setEditing(null);
        setDialogOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };
    const remove = (row) => {
        if (!window.confirm(`Delete "${row.title}"? This can't be undone.`)) return;
        onChange((credentials || []).filter((r) => r.id !== row.id));
        toast.success("Removed");
    };
    const upsert = (row) => {
        const list = credentials || [];
        const idx = list.findIndex((r) => r.id === row.id);
        if (idx >= 0) {
            const next = [...list];
            next[idx] = row;
            onChange(next);
        } else {
            onChange([...list, row]);
        }
    };

    return (
        // iter100.326 — Full redesign per user's mockup.
        // - Auto-verified badges wrapped in an emerald wash container
        //   with 2-col grid + floating "✓ VERIFIED" corner pill on each.
        // - Doctor credentials use bordered hover-glow cards.
        // - Add button as a full-width dashed ghost button.
        <div data-testid="profile-credentials-section">
            {/* ═══════ SECTION 1 · Auto-verified (emerald wash) ═══════ */}
            {(invisiqueCertified || dciRegNumber) && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 mb-6">
                    <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-800 mb-3">
                        <ShieldCheck size={12} weight="fill" />
                        Auto-verified by Invisique
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {invisiqueCertified && (
                            <AutoBadge
                                icon={ShieldCheck}
                                label="Invisique™ Certified Provider"
                                sublabel="Trained to plan + deliver aligner cases"
                            />
                        )}
                        {dciRegNumber && (
                            <AutoBadge
                                icon={ShieldCheck}
                                label={`${stateCouncil || "Dental Council of India"} Registered`}
                                sublabel={`Reg no. ${dciRegNumber}`}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* ═══════ SECTION 2 · Doctor-authored credentials ═══════ */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[14.5px] font-bold text-foreground">Your credentials</h3>
                    <span className="text-[11px] text-muted-foreground">
                        Self-declared · Shown on public microsite
                    </span>
                </div>

                {sorted.length === 0 ? (
                    <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white/60 p-6 text-center">
                        <Certificate size={22} weight="duotone" className="mx-auto text-muted-foreground/50" />
                        <p className="mt-2 text-sm font-semibold text-foreground">
                            Nothing added yet
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Fellowships, awards, or publications you&apos;d like patients to see.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {sorted.map((row) => (
                            <CredentialRow
                                key={row.id}
                                row={row}
                                onEdit={openEdit}
                                onDelete={remove}
                            />
                        ))}
                    </div>
                )}

                {/* Add Achievement button — dashed ghost, full-width */}
                <button
                    type="button"
                    onClick={openAdd}
                    className="mt-4 w-full py-3.5 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 text-slate-600 font-bold text-[13px] hover:bg-sky-50 hover:border-primary hover:text-primary transition-all inline-flex items-center justify-center gap-2"
                    data-testid="cred-add-btn"
                >
                    <Plus size={14} weight="bold" />
                    Add Achievement to Profile
                </button>
            </div>

            <CredentialDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                initial={editing}
                onSave={upsert}
            />
        </div>
    );
}
